import { useState, useMemo, useCallback } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Landmark,
  Wallet,
  TrendingUp,
  Eye,
  Filter,
  X,
  RefreshCw,
  Download,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { PageTransition } from "@/components/PageTransition";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const formatMoney = (value: number | string | undefined | null) =>
  `${Number(value || 0).toLocaleString()} FRW`;

const ACCOUNT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "Asset", label: "Asset" },
  { value: "Liability", label: "Liability" },
  { value: "Equity", label: "Equity" },
  { value: "Revenue", label: "Revenue" },
  { value: "Expense", label: "Expense" },
];

interface AccountSummary {
  totalAccounts: number;
  activeAccounts: number;
  totalAssets: number;
  currentCashBalance: number;
}

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  balance: number;
  status: string;
  createdAt: string;
  description?: string;
  parentAccountId?: number | null;
}

export default function Accounts() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    const timeout = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setTypeFilter("");
    setPage(1);
  };

  const hasActiveFilters = debouncedSearch || typeFilter;

  const { data: summaryData, isLoading: summaryLoading } = useQuery<AccountSummary>(
    ["accounts-summary"],
    async () => {
      const { data } = await api.get("/accounts/summary");
      return data.data || data;
    },
    { staleTime: 30000 }
  );

  const accountsQuery = useQuery(
    ["accounts", debouncedSearch, typeFilter, page, pageSize],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (typeFilter) params.set("type", typeFilter);
      const { data } = await api.get(`/accounts?${params.toString()}`);
      return data;
    },
    { keepPreviousData: true }
  );

  const accounts: Account[] = accountsQuery.data?.results || accountsQuery.data?.data || [];
  const pagination = accountsQuery.data?.pagination || {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  };

  const columns: ColumnDef<Account, any>[] = useMemo(
    () => [
      {
        accessorKey: "accountCode",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
        cell: ({ row }) => (
          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.accountCode}
          </span>
        ),
      },
      {
        accessorKey: "accountName",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Account Name" />,
        cell: ({ row }) => (
          <span className="font-semibold text-slate-900">{row.original.accountName}</span>
        ),
      },
      {
        accessorKey: "accountType",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        cell: ({ row }) => {
          const type = row.original.accountType;
          const toneMap: Record<string, string> = {
            Asset: "bg-blue-50 text-blue-700",
            Liability: "bg-red-50 text-red-700",
            Equity: "bg-purple-50 text-purple-700",
            Revenue: "bg-green-50 text-green-700",
            Expense: "bg-amber-50 text-amber-700",
          };
          return (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                toneMap[type] || "bg-slate-100 text-slate-600"
              )}
            >
              {type}
            </span>
          );
        },
      },
      {
        accessorKey: "balance",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Balance" />
        ),
        cell: ({ row }) => (
          <span className="font-semibold text-slate-900 tabular-nums">
            {formatMoney(row.original.balance)}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const isActive = row.original.status === "Active";
          return (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-500"
              )}
            >
              {row.original.status}
            </span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-500">
            {row.original.createdAt
              ? new Date(row.original.createdAt).toLocaleDateString()
              : "—"}
          </span>
        ),
      },
      {
        id: "actions",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="" />
        ),
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[#0A9F43] hover:bg-[#0A9F43]/10"
            onClick={() => navigate(`/finance/accounts/${row.original.id}`)}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const [pageIndex, setPageIndex] = useState(0);

  return (
    <PageTransition>
      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Accounts</h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage chart of accounts and track financial balances.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  accountsQuery.refetch();
                }}
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <RefreshCw
                  className={cn(
                    "h-4 w-4 mr-1",
                    accountsQuery.isFetching && "animate-spin"
                  )}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              title="Total Accounts"
              value={String(summaryData?.totalAccounts || 0)}
              icon={<Landmark className="h-5 w-5" />}
              tone="green"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Active Accounts"
              value={String(summaryData?.activeAccounts || 0)}
              icon={<TrendingUp className="h-5 w-5" />}
              tone="blue"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Total Assets"
              value={formatMoney(summaryData?.totalAssets || 0)}
              icon={<Wallet className="h-5 w-5" />}
              tone="purple"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Current Cash Balance"
              value={formatMoney(summaryData?.currentCashBalance || 0)}
              icon={<Wallet className="h-5 w-5" />}
              tone="amber"
              loading={summaryLoading}
            />
          </div>

          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">
                  Search
                </label>
                <Input
                  placeholder="Search accounts..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="h-9 bg-slate-50 border-slate-200"
                />
              </div>
              <div className="min-w-[170px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">
                  Account Type
                </label>
                <Select
                  value={typeFilter}
                  onValueChange={(val) => {
                    setTypeFilter(val === "all" ? "" : val);
                    setPage(1);
                    setPageIndex(0);
                  }}
                >
                  <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Accounts Table */}
          <DataTable
            columns={columns}
            data={accounts}
            isLoading={accountsQuery.isLoading}
            isFetching={accountsQuery.isFetching}
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={pagination.totalPages || 1}
            setPagination={(updater) => {
              const newState =
                typeof updater === "function"
                  ? updater({ pageIndex, pageSize })
                  : updater;
              setPageIndex(newState.pageIndex);
              setPage(newState.pageIndex + 1);
            }}
            facets={[]}
            showSearch={false}
            emptyMessage="No accounts found"
            emptyDescription="Create your first account to get started."
            enableExport={false}
            hidePagination={false}
          />
        </div>
      </div>
    </PageTransition>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  tone,
  loading,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  tone: "green" | "blue" | "purple" | "amber";
  loading?: boolean;
}) {
  const toneMap = {
    green: "bg-[#0A9F43]/10 text-[#0A9F43]",
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
  };

  const valueColor = {
    green: "text-[#0A9F43]",
    blue: "text-blue-700",
    purple: "text-purple-700",
    amber: "text-amber-700",
  };

  return (
    <Card className="border border-slate-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {title}
            </p>
            {loading ? (
              <Skeleton className="mt-3 h-7 w-28" />
            ) : (
              <p className={cn("mt-2 text-2xl font-bold tracking-tight", valueColor[tone])}>
                {value}
              </p>
            )}
          </div>
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
              toneMap[tone]
            )}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
